import { useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACT_SMS } from '../../../lib/graphql/queries/contacts';
import type { GQLMessageItem } from './useContact';

const DEFAULT_PAGE_SIZE = 10;

interface GetContactSmsData {
  contact: {
    contactId: string;
    sms: {
      items: GQLMessageItem[];
      total: number;
      hasMore: boolean;
    };
  } | null;
}

/**
 * Server-side paginated SMS thread for a contact. Fetches newest-first
 * (sortOrder DESC) so the first page is the latest N messages; older pages
 * are appended via `fetchMore`. The UI reverses for display so the newest
 * sits at the bottom (inbox style) and "load older" pulls history.
 */
export const useContactSmsGQL = (contactId: string, pageSize: number = DEFAULT_PAGE_SIZE) => {
  const { organization } = useOrganizationStore();

  const { data, loading, error, fetchMore } = useQuery<GetContactSmsData>(GET_CONTACT_SMS, {
    variables: {
      organizationId: organization?.id ?? '',
      contactId,
      limit: pageSize,
      offset: 0,
      sortBy: 'created_at',
      sortOrder: 'DESC',
    },
    skip: !organization?.id || !contactId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const items = data?.contact?.sms?.items ?? [];
  const total = data?.contact?.sms?.total ?? 0;
  const hasMore = data?.contact?.sms?.hasMore ?? false;

  const loadOlder = useCallback(() => {
    if (!hasMore || loading) return;
    return fetchMore({
      variables: { offset: items.length, limit: pageSize },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.contact?.sms) return prev;
        const prevItems = prev?.contact?.sms?.items ?? [];
        return {
          contact: {
            ...fetchMoreResult.contact,
            sms: {
              ...fetchMoreResult.contact.sms,
              items: [...prevItems, ...fetchMoreResult.contact.sms.items],
            },
          },
        };
      },
    });
  }, [fetchMore, hasMore, loading, items.length, pageSize]);

  return { items, total, hasMore, loading, error, loadOlder };
};
